import * as React from 'react'

// import apis from '@app/api/api'

import useI18n from '@store/i18n/useI18n'
import Theme from '@theme/Theme'
import styled from '@theme/styled-components'
import { FiChevronRight, FiCheck } from 'react-icons/fi'
import { RiInformationLine } from 'react-icons/ri'

import { ImWarning } from 'react-icons/im'

import Loader from 'react-loader-spinner'
import PictureFilters from '@components/filter/pictureFilters'

interface Props {
  actualTab: Tab
  steps: BusinessStep[]
  navigation: Navigation
  screenStatus: ScreenStatus
  business?: AllBusinessDetails
  setBusiness: (b: AllBusinessDetails) => void
  declarations: BusinessDeclaration[]
  setActualTab: (t: Tab) => void
  allPictures: BusinessDetailsPicture[]
  setSectionPictures: (p: BusinessDetailsPicture[]) => void
}

const BusinessDetailsMenu = ({
  steps,
  business,
  declarations,
  actualTab,
  navigation,
  allPictures,
  setActualTab,
  screenStatus,
  setSectionPictures,
}: Props) => {
  const i18n = useI18n()

  const businessBreakpoints = React.useMemo(() => declarations.filter((d) => !!d.breakpoint), [declarations])
  const breakpointHasUnresolvedBreakpoint = businessBreakpoints.find((b) => !!b.breakpoint && !b.breakpoint.release)

  const renderTab = (targetTab: Tab) => {
    return (
      <>
        <TabItem
          selected={actualTab === targetTab}
          onClick={() => setActualTab(targetTab)}
          hasLegende={
            (actualTab === 'pictures' && targetTab === 'pictures') || (actualTab === 'state' && targetTab === 'state')
          }>
          <TabTitleContainer>
            {targetTab === 'state' && businessBreakpoints.length !== 0 && (
              <TabTitleIcon>
                {breakpointHasUnresolvedBreakpoint ? (
                  <ImWarning size={20} color={Theme.colors.primary_blue} />
                ) : (
                  <FiCheck size={20} color={Theme.colors.primary_green} />
                )}
              </TabTitleIcon>
            )}
            <TabTitle selected={actualTab === targetTab}>
              {i18n.t(`components.businessDetails.${targetTab}Tab`)}
            </TabTitle>
          </TabTitleContainer>
          {actualTab === targetTab && <FiChevronRight size={26} color={Theme.colors.primary_blue} />}
        </TabItem>
        {actualTab === 'pictures' && targetTab === 'pictures' && (
          <FilterContainer>
            <PictureFilters steps={steps} allPictures={allPictures} setSectionPictures={setSectionPictures} />
          </FilterContainer>
        )}
        {actualTab === 'state' && targetTab === 'state' && (
          <LegendeContainer>
            <LegendeLine>
              <RiInformationLine size={20} color={Theme.colors.primary_blue} />
              <LegendeText>{i18n.t('components.businessDetails.legende.information')}</LegendeText>
            </LegendeLine>
            <LegendeLine>
              <ImWarning size={20} color={Theme.colors.secondary_yellow} />
              <LegendeText>{i18n.t('components.businessDetails.legende.warning')}</LegendeText>
            </LegendeLine>
            <LegendeLine>
              <FiCheck size={20} color={Theme.colors.primary_green} />
              <LegendeText>{i18n.t('components.businessDetails.legende.check')}</LegendeText>
            </LegendeLine>
          </LegendeContainer>
        )}
      </>
    )
  }

  return (
    <Container>
      <ScreenTitleContainer>
        <ScreenTitle>
          {screenStatus === 'loading' || !business
            ? i18n.t('components.detailsChantier.title.loading')
            : i18n.t(`components.detailsChantier.title.${business.state}`, { title: business.title })}
        </ScreenTitle>
        <Back onClick={() => navigation.goBack()}>{i18n.t('components.menu.back')}</Back>
      </ScreenTitleContainer>
      {screenStatus !== 'fetched' || !business ? (
        screenStatus === 'loading' ? (
          <StatusContainer>
            <Loader type="ThreeDots" color={Theme.colors.classicButtonLoader} height={30} />
          </StatusContainer>
        ) : (
          <ErrorContainer>
            <ErrorLibelle>{i18n.t('components.detailsChantier.error')}</ErrorLibelle>
          </ErrorContainer>
        )
      ) : (
        <>
          <Subtitle>{business.address}</Subtitle>
          <Subtitle>{business.subtitle}</Subtitle>
          {!!business.user && (
            <Subtitle>
              <DetailsTitle>{i18n.t('components.detailsChantier.businessManager')}</DetailsTitle>
              <HorizontalSeparator />
              <div>{`${business.user.lastname} ${business.user.firstname}`}</div>
            </Subtitle>
          )}
          {!!business.companies && (
            <Subtitle>
              <DetailsTitle>{i18n.t('components.detailsChantier.companies')}</DetailsTitle>
              <HorizontalSeparator />
              <div>{business.companies.map((company) => company.name).join(', ')}</div>
            </Subtitle>
          )}
          <TabContainer>
            {renderTab('state')}
            {renderTab('pictures')}
            {renderTab('comments')}
          </TabContainer>
        </>
      )}
    </Container>
  )
}

export default BusinessDetailsMenu

const Container = styled('div')`
  flex: 1;
  display: flex;
  overflow: hidden;
  margin-bottom: 10px;
  flex-direction: column;
  overflow-y: scroll;
  padding: 10px;
`
const StatusContainer = styled('div')`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
`
const ErrorContainer = styled('div')`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`
const ErrorLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
  margin: 40px;
`
const Back = styled('div')`
  ${(props) => props.theme.fonts.backLibelle};
  cursor: pointer;
`
const ScreenTitle = styled('div')`
  ${(props) => props.theme.fonts.title};
`
const ScreenTitleContainer = styled('div')`
  border: 0px solid ${(props) => props.theme.colors.menuBorder};
  border-bottom-width: 1px;
  border-top-width: 1px;
  padding: 15px 0;

  display: flex;
  align-items: center;
  justify-content: space-between;
`
const Subtitle = styled('div')`
  ${(props) => props.theme.fonts.chantierMenuSubtitle};
  display: flex;
  align-items: center;

  border: 0px solid ${(props) => props.theme.colors.menuBorder};
  border-bottom-width: 1px;
  padding: 20px 0px;
  margin: 0px 20px;
`
const TabContainer = styled('div')`
  margin-top: 40px;
  padding-bottom: 40px;
`
const TabItem = styled('div')<{ selected: boolean; hasLegende?: boolean }>`
  cursor: pointer;
  height: 50px;
  padding: 0px 20px;
  background: ${(props) =>
    props.selected ? props.theme.colors.backgroundSelected : props.theme.colors.filterBackgroundColor};
  ${(props) => !props.hasLegende && 'border-radius: 20px;'}
  ${(props) => !!props.hasLegende && 'border: 0px solid;'}
    ${(props) => !!props.hasLegende && 'border-top-right-radius: 20px;'}
    ${(props) => !!props.hasLegende && 'border-top-left-radius: 20px;'}
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${(props) => !props.hasLegende && 'margin-bottom: 12px;'}
`
const TabTitleContainer = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`
const TabTitleIcon = styled('div')`
  display: flex;
  margin-right: 10px;
  align-items: center;
  justify-content: center;
`
const TabTitle = styled('div')<{ selected: boolean }>`
  ${(props) => (props.selected ? props.theme.fonts.boldLabel : props.theme.fonts.label)};
  color: ${(props) => (props.selected ? props.theme.colors.primary_blue : props.theme.colors.secondary_grey)};
`
const FilterContainer = styled('div')`
  margin-bottom: 12px;
`
const LegendeContainer = styled('div')`
  padding: 25px;
  display: flex;
  margin-bottom: 12px;
  flex-direction: column;
  background: ${(props) => props.theme.colors.filterBackgroundColor};
`
const LegendeLine = styled('div')`
  margin: 5px 2px;
  display: flex;
`
const LegendeText = styled('div')`
  margin: 2px;
  display: flex;
  margin-left: 20px;
  ${(props) => props.theme.fonts.label};
  color: ${(props) => props.theme.colors.secondary_grey};
`
const DetailsTitle = styled('div')`
  ${(props) => props.theme.fonts.boldLabel};
  color: ${(props) => props.theme.colors.primary_blue};
`
const HorizontalSeparator = styled('div')`
  height: 1px;
  width: 7px;
  background-color: ${(props) => props.theme.colors.libelleColor};
  margin: 0px 7px;
`
